import React from 'react';
import { View, Pressable, Platform, ScrollView, Linking } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ContactForm from '@/components/Forms/ContactForm';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import FlowField from '@/components/Landing/FlowField';
import useResponsiveness from '@/hooks/useResponsiveness';
import { isDarkTheme } from '@/constants/Colors';

// Contact information constants
const CONTACT_INFO = [
  { 
    icon: 'envelope', 
    text: 'ai@gab.com',
    link: 'mailto:ai@gab.com',
    description: "Email us for support",
    accessibilityLabel: "Email support",
  },
  { 
    icon: 'frog',
    text: '@gab_ai',
    link: 'https://gab.com/gab_ai',
    description: "Follow us on Gab",
    accessibilityLabel: "Gab Social profile",
  },
  { 
    icon: 'x', 
    text: '@Gab__AI',
    link: 'https://x.com/Gab__AI',
    description: "Follow us on X",
    accessibilityLabel: "X (Twitter) profile",
  },
];

// ============================================================================
// CONTACT INFO CARD COMPONENT
// ============================================================================
const ContactInfoCard = React.memo(({ item, isDark, isXS }) => (
  <Pressable
    style={{
      flex: 1,
      minWidth: isXS ? '100%' : 200,
      maxWidth: isXS ? '100%' : 280,
      padding: isXS ? 20 : 24,
      borderRadius: 20,
      backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
      cursor: 'pointer',
      transitionProperty: 'transform, box-shadow',
      transitionDuration: '0.2s',
    }}
    onPress={() => {
      if (Platform.OS === 'web') {
        window.open(item.link, '_blank');
      } else {
        Linking.openURL(item.link);
      }
    }}
    accessibilityRole="link"
    accessibilityLabel={item.accessibilityLabel}
    accessibilityHint={`Opens ${item.link} in a new tab`}
  >
    <View
      style={{
        width: 48,
        height: 48,
        borderRadius: 14,
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      <Icon name={item.icon} size={20} color={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'} solid />
    </View>
    <Text tag="span" size="m" weight="medium" style={{ marginBottom: 6, letterSpacing: -0.2 }}>
      {item.text}
    </Text>
    <Text tag="span" size="s" color="secondary" style={{ lineHeight: 20 }}>
      {item.description}
    </Text>
  </Pressable>
));

// ============================================================================
// MAIN CONTACT PAGE
// ============================================================================
export default function ContactPage() {
  const { colors, theme } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  const sectionStyle = {
    width: '100%',
    alignItems: 'center',
    paddingHorizontal: isXS ? 20 : 40,
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead 
        title="Contact Us"
        description="Contact Gab AI support team. Get help, ask questions, or discuss enterprise AI solutions. Email ai@gab.com or reach us in Clarks Summit, PA."
        canonical="/contact"
        keywords="contact Gab AI, Gab AI support, AI help, enterprise AI, customer service, Gab AI email"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact Gab AI',
          description: 'Contact Gab AI support team for help, questions, or enterprise solutions.',
          url: 'https://gab.ai/contact',
          mainEntity: {
            '@type': 'Organization',
            name: 'Gab AI Inc.',
            email: 'ai@gab.com',
            telephone: '',
            address: {
              '@type': 'PostalAddress',
              streetAddress: '1143 Northern Blvd #158',
              addressLocality: 'Clarks Summit',
              addressRegion: 'PA',
              postalCode: '18411',
              addressCountry: 'US',
            },
          },
        }}
      />

      <ScrollView
        accessibilityRole="main"
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ================================================================ */}
        {/* HERO SECTION */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingTop: isXS ? 100 : 140,
              paddingBottom: isXS ? 40 : 60,
              position: 'relative',
              overflow: 'hidden',
            },
          ]}
        >
          {/* Hero Content */}
          <View style={{ maxWidth: 700, alignItems: 'center', zIndex: 1 }}>
            <Text
              tag="p"
              size="s"
              weight="medium"
              style={{
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)',
                textTransform: 'uppercase',
                letterSpacing: 2,
                marginBottom: 24,
                fontSize: 13,
              }}
            >
              Contact Us
            </Text>

            <Text
              tag="h1"
              weight="bold"
              style={{
                fontSize: isXS ? 32 : isSM ? 44 : 52,
                textAlign: 'center',
                marginBottom: 20,
                lineHeight: isXS ? 40 : isSM ? 52 : 60,
                letterSpacing: -1.5,
              }}
            >
              {t('contact.title')}
            </Text>
            <Text
              tag="p"
              size="l"
              color="secondary"
              style={{
                textAlign: 'center',
                fontSize: isXS ? 16 : 18,
                lineHeight: isXS ? 26 : 30,
                maxWidth: 560,
              }}
            >
              {t('contact.subtitle')}
            </Text>
          </View>
        </View>

        {/* ================================================================ */}
        {/* CONTACT FORM SECTION */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 40 : 60,
            },
          ]}
        >
          <View
            style={{
              width: '100%',
              maxWidth: 600,
              padding: isXS ? 24 : 32,
              borderRadius: 28,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 24 }}>
              <View
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name="paper-plane" size={18} color={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'} solid />
              </View>
              <View>
                <Text tag="h2" size="xl" weight="medium" style={{ letterSpacing: -0.3 }}>
                  Send us a Message
                </Text>
                <Text tag="p" size="s" color="secondary">
                  We typically respond within 24 hours
                </Text>
              </View>
            </View>
            <ContactForm />
          </View>
        </View>

        {/* ================================================================ */}
        {/* OTHER WAYS TO REACH US */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 40 : 80,
            },
          ]}
        >
          <View style={{ maxWidth: 900, width: '100%', alignItems: 'center' }}>
            <Text
              tag="h2"
              size="xxxl"
              weight="medium"
              style={{
                marginBottom: 12,
                fontSize: isXS ? 24 : 32,
                letterSpacing: -0.5,
                textAlign: 'center',
              }}
            >
              {t('contact.otherWaysTitle')}
            </Text>
            <Text
              tag="p"
              size="m"
              color="secondary"
              style={{ 
                lineHeight: isXS ? 24 : 28, 
                marginBottom: isXS ? 32 : 48, 
                textAlign: 'center', 
                maxWidth: 500 
              }}
            >
              Choose the channel that works best for you
            </Text>

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 16,
                justifyContent: 'center',
                maxWidth: 900,
              }}
              accessibilityRole="list"
              accessibilityLabel="Contact information list"
            >
              {CONTACT_INFO.map((item, index) => (
                <ContactInfoCard
                  key={index}
                  item={item}
                  isDark={isDark}
                  isXS={isXS}
                />
              ))}
            </View>
          </View>
        </View>

        {/* ================================================================ */}
        {/* FAQ / QUICK HELP SECTION */}
        {/* ================================================================ */}
        <View
          style={[
            sectionStyle,
            {
              paddingVertical: isXS ? 40 : 60,
            },
          ]}
        >
          <View
            style={{
              maxWidth: 600,
              width: '100%',
              padding: isXS ? 28 : 36,
              borderRadius: 24,
              backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
              flexDirection: isXS ? 'column' : 'row',
              alignItems: isXS ? 'flex-start' : 'center',
              gap: isXS ? 20 : 24,
            }}
          >
            <View
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="lightbulb" size={24} color={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'} solid />
            </View>
            <View style={{ flex: 1 }}>
              <Text tag="h3" size="l" weight="medium" style={{ marginBottom: 8, letterSpacing: -0.2 }}>
                Looking for Quick Answers?
              </Text>
              <Text tag="p" size="m" color="secondary" style={{ lineHeight: 24 }}>
                Check out our documentation for guides, tutorials, and answers to frequently asked questions about using Gab AI.
              </Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <PublicFooter />
      </ScrollView>
    </View>
  );
}
